import { createSignal, batch } from 'solid-js';
import type { Game, Round } from '../game/Game';
import { RoundState } from '../game/Game';
import { STORAGE_KEY_BALANCE, DEFAULT_USERNAME, DEFAULT_AVATAR, MAX_HISTORY } from '../game/constants';
import { UserService, GameService } from '../services';
import type { UserProfile } from '../services';
import { gameApi, getGameInstance, isRealMode } from '../api';
import type {
  GameApi, SessionInitPayload, RoundStatePayload, RoundCrashedPayload,
  BetPlaceAckPayload, BetCancelAckPayload, BetCashoutAckPayload,
  AutoCashoutPayload, PlayerStatsPayload, BetHistoryPayload,
  LeaderboardPayload, RoundBetSnapshot, StreamerRoomUpdatePayload,
  StreamerListPayload,
  PlayersOnlinePayload,
} from '../api';

const [engine, setEngine] = createSignal<Game | undefined>(undefined);
const [phase, setPhase] = createSignal<RoundState>(RoundState.BET_TIME);
const [multiplier, setMultiplier] = createSignal<number>(1.0);
const [balance, setBalance] = createSignal<number>(0);
const [balanceLoaded, setBalanceLoaded] = createSignal(false);
const [profile, setProfile] = createSignal<UserProfile>({ username: DEFAULT_USERNAME, avatar: DEFAULT_AVATAR });
const [profileLoaded, setProfileLoaded] = createSignal(false);
const [roundHistory, setRoundHistory] = createSignal<Round[]>([]);
const [currentRound, setCurrentRound] = createSignal<Round | null>(null);
const [connected, setConnected] = createSignal(false);
const [playerId, setPlayerId] = createSignal<number | null>(null);
const [playerStats, setPlayerStats] = createSignal<PlayerStatsPayload | null>(null);
const [betHistory, setBetHistory] = createSignal<BetHistoryPayload | null>(null);
const [leaderboard, setLeaderboard] = createSignal<LeaderboardPayload | null>(null);
const [roundBets, setRoundBets] = createSignal<RoundBetSnapshot[]>([]);
const [roundNumber, setRoundNumber] = createSignal(0);
const [roundId, setRoundId] = createSignal<number | null>(null);
const [streamerRoomUpdate, setStreamerRoomUpdate] = createSignal<StreamerRoomUpdatePayload | null>(null);
const [streamerList, setStreamerList] = createSignal<StreamerListPayload | null>(null);
const [onlinePlayers, setOnlinePlayers] = createSignal<PlayersOnlinePayload | null>(null);
const [demoMode, setDemoMode] = createSignal(true);
const [demoLabel, setDemoLabel] = createSignal('DEMO');

// Provably fair signals
const [serverSeedHash, setServerSeedHash] = createSignal<string>('');
const [fairClientSeed, setFairClientSeed] = createSignal<string | null>(null);
const [fairNonce, setFairNonce] = createSignal<number | null>(null);
const [revealedServerSeed, setRevealedServerSeed] = createSignal<string | null>(null);

// Round timing
const [roundStartsAt, setRoundStartsAt] = createSignal<number | null>(null);

// Last crash data (for provably fair verification)
const [lastCrash, setLastCrash] = createSignal<RoundCrashedPayload | null>(null);

let engineConnected = false;

type HistorySeedRound = {
  roundId: number;
  roundNumber?: number | null;
  crashPoint?: number | null;
  multiplier?: number;
  timestamp?: number;
};

function toHistoryRound(seed: HistorySeedRound, fallbackIndex: number): Round | null {
  const roundId = Number(seed.roundId);
  if (!Number.isFinite(roundId)) return null;

  const crashPoint = Number(seed.crashPoint ?? seed.multiplier ?? 1);
  if (!Number.isFinite(crashPoint) || crashPoint <= 0) return null;

  const safeRoundNumber = Number(seed.roundNumber ?? roundId);
  const roundNumber = Number.isFinite(safeRoundNumber) ? safeRoundNumber : roundId;
  const ts = Number(seed.timestamp) || (Date.now() - (fallbackIndex * 1000));

  return {
    id: `ws_round_${roundId}`,
    state: RoundState.CRASH,
    startTime: ts,
    crashPoint,
    multiplier: crashPoint,
    bets: [],
    serverSeed: '',
    clientSeed: '',
    gameSeed: `history_${roundNumber}`,
  };
}

function applyHistorySeeds(seeds: HistorySeedRound[]): void {
  const mapped: Round[] = [];
  for (let i = 0; i < seeds.length; i++) {
    const item = toHistoryRound(seeds[i], i);
    if (item) mapped.push(item);
  }
  if (!mapped.length) return;
  setRoundHistory(mapped.slice(0, MAX_HISTORY));
}

function extractSessionHistory(payload: SessionInitPayload): HistorySeedRound[] {
  return Array.isArray(payload.roundHistory) ? payload.roundHistory : [];
}

// ── Legacy Engine binding (mock mode) ────────────────

function connectEngine(g: Game): void {
  if (engineConnected) return;
  engineConnected = true;
  setEngine(g);

  setPhase(g.getCurrentState());
  setMultiplier(g.getCurrentMultiplier());
  setCurrentRound(g.getCurrentRound());
  setRoundHistory(g.getRoundHistory());

  g.on('stateChange', (e) => {
    setPhase(e.data.state);
  });

  g.on('multiplierUpdate', (e) => {
    setMultiplier(e.data.multiplier);
  });

  g.on('roundStart', () => {
    setCurrentRound(g.getCurrentRound());
  });

  g.on('crash', () => {
    setCurrentRound(g.getCurrentRound());
    const round = g.getCurrentRound();
    if (round) {
      GameService.recordCrash({
        roundId: round.id,
        crashPoint: round.crashPoint,
        timestamp: Date.now(),
      });
    }
  });

  g.on('historyUpdate', () => {
    setRoundHistory([...g.getRoundHistory()]);
  });
}

// ── GameApi binding (both modes) ─────────────────────

function connectGameApi(api: GameApi): void {
  api.on('session:init', (payload: SessionInitPayload) => {
    batch(() => {
      setPlayerId(payload.playerId);
      setBalance(payload.balance.amount);
      setBalanceLoaded(true);
      setProfile({
        username: payload.username,
        avatar: payload.avatar || DEFAULT_AVATAR,
      });
      setProfileLoaded(true);
      setConnected(true);

      if (typeof payload.demoMode === 'boolean') {
        setDemoMode(payload.demoMode);
      }
      if (typeof payload.demoLabel === 'string' && payload.demoLabel.trim()) {
        setDemoLabel(payload.demoLabel.trim());
      }

      const initHistory = extractSessionHistory(payload);
      if (initHistory.length) {
        applyHistorySeeds(initHistory);
      }

      if (payload.roundSnapshot) {
        applyRoundSnapshot(payload.roundSnapshot);
      }

      setOnlinePlayers({
        totalOnline: Math.max(0, Math.floor(payload.totalOnline)),
        updatedAt: Number(payload.serverTime) || Date.now(),
      });
    });
  });

  api.on('round:state', (payload: RoundStatePayload) => {
    const phaseMap: Record<string, RoundState> = {
      BET_TIME: RoundState.BET_TIME,
      RUNNING: RoundState.RUNNING,
      CRASH: RoundState.CRASH,
    };
    const mapped = phaseMap[payload.state] ?? RoundState.BET_TIME;
    batch(() => {
      setPhase(mapped);
      setMultiplier(payload.multiplier);
      setRoundBets(payload.bets || []);
      setRoundNumber(payload.roundNumber);
      setRoundId(payload.roundId);
      setRoundStartsAt(payload.startsAt);
      setServerSeedHash(payload.serverSeedHash || '');
      setFairClientSeed(payload.clientSeed);
      setFairNonce(payload.nonce);
      // Clear revealed seed on new round
      if (payload.state === 'BET_TIME') {
        setRevealedServerSeed(null);
      }
    });
  });

  api.on('tick', (payload) => {
    setMultiplier(payload.multiplier);
  });

  api.on('round:crashed', (payload: RoundCrashedPayload) => {
    batch(() => {
      setPhase(RoundState.CRASH);
      setLastCrash(payload);
      setRevealedServerSeed(payload.serverSeed);
      setServerSeedHash(payload.serverSeedHash);
      setFairClientSeed(payload.clientSeed);
      setFairNonce(payload.nonce);
    });

    const crashedRound = toHistoryRound(
      {
        roundId: payload.roundId,
        roundNumber: payload.roundNumber,
        crashPoint: payload.crashPoint,
        multiplier: payload.crashPoint,
      },
      0,
    );

    if (crashedRound) {
      setRoundHistory((prev) => {
        if (prev.some((round) => round.id === crashedRound.id)) return prev;
        return [crashedRound, ...prev].slice(0, MAX_HISTORY);
      });
    }

    // In real mode, record crash for local history display
    if (isRealMode) {
      GameService.recordCrash({
        roundId: String(payload.roundId),
        crashPoint: payload.crashPoint,
        timestamp: payload.timestamp,
      });
    }
  });

  api.on('balance:update', (payload) => {
    setBalance(payload.amount);
    setBalanceLoaded(true);
  });

  api.on('connection:status', (payload) => {
    setConnected(payload.connected);
  });

  // Bet ack handlers — update balance immediately from server response
  api.on('bet:place', (payload: BetPlaceAckPayload) => {
    setBalance(payload.newBalance);
  });

  api.on('bet:cancel', (payload: BetCancelAckPayload) => {
    setBalance(payload.newBalance);
  });

  api.on('bet:cashout', (payload: BetCashoutAckPayload) => {
    setBalance(payload.newBalance);
  });

  // Stats / history / leaderboard responses — populate signals
  api.on('player:stats', (payload: PlayerStatsPayload) => {
    setPlayerStats(payload);
  });

  api.on('player:betHistory:data', (payload: BetHistoryPayload) => {
    setBetHistory(payload);
  });

  api.on('leaderboard:data', (payload: LeaderboardPayload) => {
    setLeaderboard(payload);
  });

  // Streamer events
  api.on('streamer:roomUpdate', (payload: StreamerRoomUpdatePayload) => {
    setStreamerRoomUpdate(payload);
  });

  api.on('streamer:list', (payload: StreamerListPayload) => {
    setStreamerList(payload);
  });

  api.on('players:online', (payload: PlayersOnlinePayload) => {
    const totalOnline = Number.isFinite(payload.totalOnline)
      ? Math.max(0, Math.floor(payload.totalOnline))
      : 0;
    const updatedAt = Number.isFinite(payload.updatedAt)
      ? payload.updatedAt
      : Date.now();
    setOnlinePlayers({ totalOnline, updatedAt });
  });

  // Global error handler — show toast for all WS errors
  api.on('error', (payload) => {
    const fn = (window as any).__memePumpShowToast;
    if (typeof fn === 'function') fn('error', payload.message || 'Server error');
  });
}

function applyRoundSnapshot(snap: RoundStatePayload): void {
  const phaseMap: Record<string, RoundState> = {
    BET_TIME: RoundState.BET_TIME,
    RUNNING: RoundState.RUNNING,
    CRASH: RoundState.CRASH,
  };
  batch(() => {
    setPhase(phaseMap[snap.state] ?? RoundState.BET_TIME);
    setMultiplier(snap.multiplier);
    setRoundBets(snap.bets || []);
    setRoundNumber(snap.roundNumber);
    setRoundId(snap.roundId);
    setRoundStartsAt(snap.startsAt);
    setServerSeedHash(snap.serverSeedHash || '');
    setFairClientSeed(snap.clientSeed);
    setFairNonce(snap.nonce);
  });
}

// ── Balance persistence (legacy compat) ──────────────

function readStoredBalance(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_BALANCE);
    if (stored) {
      setBalance(Number(stored));
      setBalanceLoaded(true);
    }
  } catch { }
}

function interceptBalanceWrites(): void {
  const nativeSetItem = localStorage.setItem.bind(localStorage);
  localStorage.setItem = function (key: string, value: string) {
    nativeSetItem(key, value);
    if (key === STORAGE_KEY_BALANCE) {
      const num = Number(value);
      if (isFinite(num)) setBalance(num);
    }
  };
}

// ── Profile ──────────────────────────────────────────

async function loadProfile(): Promise<void> {
  try {
    const result = await UserService.getProfile();
    batch(() => {
      setProfile(result);
      setProfileLoaded(true);
    });
  } catch { }
}

async function updateProfile(next: UserProfile): Promise<void> {
  setProfile(next);
  await UserService.updateProfile(next);
  gameApi.updateProfile({
    screenName: next.username,
    avatar: next.avatar,
  });
}

// ── Initialization ───────────────────────────────────

// Wire the GameApi events first
connectGameApi(gameApi);

// Connect the API (starts mock game loop or WS connection)
gameApi.connect();

// In mock mode, also wire the legacy engine binding
if (!isRealMode) {
  const game = getGameInstance();
  if (game) {
    connectEngine(game);
  }
  readStoredBalance();
  interceptBalanceWrites();
}

// In mock mode, load profile from localStorage; in real mode session:init already provides it
if (!isRealMode) {
  loadProfile();
}

// Legacy: resolve engine from window.memePumpApp (for components that need it)
function resolveEngine(): void {
  if (engineConnected) return;
  const app = (window as any).memePumpApp as { getGame: () => Game } | undefined;
  if (app) connectEngine(app.getGame());
}

if (!engineConnected && !isRealMode) {
  const onReady = () => {
    resolveEngine();
    window.removeEventListener('memePump:ready', onReady);
  };
  window.addEventListener('memePump:ready', onReady);
}

export const gameStore = {
  engine,
  phase,
  multiplier,
  balance,
  balanceLoaded,
  profile,
  profileLoaded,
  roundHistory,
  currentRound,
  connected,
  playerId,
  playerStats,
  betHistory,
  leaderboard,
  roundBets,
  roundNumber,
  roundId,
  streamerRoomUpdate,
  streamerList,
  onlinePlayers,
  demoMode,
  demoLabel,
  serverSeedHash,
  fairClientSeed,
  fairNonce,
  revealedServerSeed,
  roundStartsAt,
  lastCrash,
  updateProfile,
  connectEngine,
};

export type GameStore = typeof gameStore;
